import { take } from 'lib/array/arrayUtils'
import type { SyntheticEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { GalleryImageCount } from '../components/TripPlanDetailDrawer.styles'

const DRAWER_GALLERY_PREVIEW_IMAGE_COUNT = 3

export interface DrawerGalleryImage extends App.Image {
  id?: string
  url?: string
}

export interface DrawerGalleryGridImage<Image extends DrawerGalleryImage> {
  image: Image
  lightboxIndex: number
}

interface UseDrawerGalleryImagesArgs<Image extends DrawerGalleryImage> {
  images: Array<Image>
  resetKey: string
  isGridImage?: (image: Image) => boolean
  previewImageCount?: number
}

const isGridImageByDefault = () => true

export function getDrawerGalleryImageKey(image: DrawerGalleryImage): string | undefined {
  return image.id ?? image.url
}

export function useDrawerGalleryImages<Image extends DrawerGalleryImage>({
  images,
  resetKey,
  isGridImage = isGridImageByDefault,
  previewImageCount = DRAWER_GALLERY_PREVIEW_IMAGE_COUNT,
}: UseDrawerGalleryImagesArgs<Image>) {
  const [failedImageKeys, setFailedImageKeys] = useState<Array<string>>([])

  useEffect(() => {
    setFailedImageKeys([])
  }, [resetKey])

  const availableImages = useMemo(() => {
    return images.filter((image) => {
      const imageKey = getDrawerGalleryImageKey(image)
      return imageKey && !failedImageKeys.includes(imageKey)
    })
  }, [failedImageKeys, images])

  const gridImages = useMemo(() => {
    const gridImageItems = availableImages.map<DrawerGalleryGridImage<Image> | undefined>((image, lightboxIndex) => {
      if (!isGridImage(image)) return undefined

      return { image, lightboxIndex }
    }).filter((image): image is DrawerGalleryGridImage<Image> => image !== undefined)

    return take(gridImageItems, previewImageCount)
  }, [availableImages, isGridImage, previewImageCount])

  const imageCount: GalleryImageCount | undefined = gridImages.length === 0
    ? undefined
    : gridImages.length as GalleryImageCount

  const handleImageError = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    const imageKey = event.currentTarget.dataset.imageKey
    if (!imageKey) return

    setFailedImageKeys((currentImageKeys) => currentImageKeys.includes(imageKey)
      ? currentImageKeys
      : [...currentImageKeys, imageKey])
  }, [])

  return {
    availableImages,
    failedImageKeys,
    gridImages,
    imageCount,
    handleImageError,
  }
}
